import React, { useState, useRef, useEffect } from 'react';
import { X } from 'lucide-react';
import { supabase, isSupabaseConfigured } from './lib/supabase';

const App = () => {
  const [artists, setArtists] = useState([]);
  const [newName, setNewName] = useState('');
  const [draggingIndex, setDraggingIndex] = useState(null);
  const [draggingId, setDraggingId] = useState(null);
  const [saveStatus, setSaveStatus] = useState('');
  const svgRef = useRef(null);

  useEffect(() => {
    const loadArtists = async () => {
      try {
        const { data, error } = await supabase
          .from('artists')
          .select('*')
          .order('created_at', { ascending: true });

        if (error) throw error;
        
        if (data) {
          setArtists(data);
        }
      } catch (error) {
        console.error('Error loading artists:', error);
        setSaveStatus('error');
        setTimeout(() => setSaveStatus(''), 2000);
      }
    };
    loadArtists();
  }, []);

  useEffect(() => {
    const channel = supabase
      .channel('artists-changes')
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: 'artists' },
        async (payload) => {
          // Reload artists when changes occur from other users
          const { data, error } = await supabase
            .from('artists')
            .select('*')
            .order('created_at', { ascending: true });

          if (!error && data) {
            setArtists(data);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const addArtist = async () => {
    if (newName.trim()) {
      try {
        const { data, error } = await supabase
          .from('artists')
          .insert([{ name: newName.trim(), x: 200, y: 500 }])
          .select()
          .single();

        if (error) throw error;

        if (data) {
          setArtists([...artists, data]);
          setNewName('');
          setSaveStatus('saved');
          setTimeout(() => setSaveStatus(''), 2000);
        }
      } catch (error) {
        console.error('Error adding artist:', error);
        setSaveStatus('error');
        setTimeout(() => setSaveStatus(''), 2000);
      }
    }
  };

  const removeArtist = async (id) => {
    try {
      const { error } = await supabase
        .from('artists')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setArtists(artists.filter(artist => artist.id !== id));
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus(''), 2000);
    } catch (error) {
      console.error('Error removing artist:', error);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus(''), 2000);
    }
  };

  const handleMouseDown = (index, id) => {
    setDraggingIndex(index);
    setDraggingId(id);
  };

  const handleMouseMove = (e) => {
    if (draggingIndex === null || draggingId === null) return;

    const svg = svgRef.current;
    const point = svg.createSVGPoint();
    point.x = e.clientX;
    point.y = e.clientY;
    const svgPoint = point.matrixTransform(svg.getScreenCTM().inverse());

    const x = Math.max(100, Math.min(900, svgPoint.x));
    const y = Math.max(0, Math.min(600, svgPoint.y));

    setArtists(prev => {
      const updated = prev.map((artist, i) => 
        i === draggingIndex ? { ...artist, x, y } : artist
      );
      return updated;
    });
  };

  const handleMouseUp = async () => {
    if (draggingIndex !== null && draggingId !== null) {
      const artist = artists[draggingIndex];
      try {
        const { error } = await supabase
          .from('artists')
          .update({ x: artist.x, y: artist.y })
          .eq('id', draggingId);

        if (error) throw error;

        setSaveStatus('saved');
        setTimeout(() => setSaveStatus(''), 2000);
      } catch (error) {
        console.error('Error updating artist position:', error);
        setSaveStatus('error');
        setTimeout(() => setSaveStatus(''), 2000);
      }
    }
    setDraggingIndex(null);
    setDraggingId(null);
  };

  const getGridPosition = (artist) => {
    const gridX = Math.floor((artist.x - 100) / 200);
    const gridY = Math.floor(artist.y / 200);
    return { gridX: Math.max(0, Math.min(3, gridX)), gridY: Math.max(0, Math.min(2, gridY)) };
  };

  const xPositions = ['Ideation', 'Direction', 'World Build', 'Rollout'];
  const yPositions = ['Pro', 'Dev', 'Flow'];

  return (
    <div 
      className="flex flex-col items-center p-4 md:p-8 bg-slate-950 min-h-screen"
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
    >
      {!isSupabaseConfigured && (
        <div className="w-full max-w-6xl mb-4 p-4 bg-red-900/50 border border-red-700 rounded-lg text-red-200">
          <p className="font-bold">⚠️ Supabase not configured</p>
          <p className="text-sm mt-1">Please set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY environment variables in Vercel.</p>
        </div>
      )}
      <div className="max-w-6xl w-full">
        <div className="text-center mb-8">
          <h1 className="text-7xl font-black mb-3 text-slate-200 tracking-tight">
            Artist Development Matrix
          </h1>
          <p className="text-slate-400 text-lg">Drag artists anywhere to visualize their journey</p>
        </div>
        
        <div className="relative w-full bg-slate-900/50 backdrop-blur-xl rounded-3xl shadow-2xl border border-slate-800 p-8 mb-6">
          <div className="relative rounded-2xl overflow-hidden shadow-inner" style={{ paddingBottom: '75%' }}>
            <svg ref={svgRef} className="absolute inset-0 w-full h-full" viewBox="0 0 900 700">
              <defs>
                <linearGradient id="borderGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#ff6b6b" />
                  <stop offset="50%" stopColor="#ffff6b" />
                  <stop offset="100%" stopColor="#6bffad" />
                </linearGradient>
                <filter id="dropShadow" x="-50%" y="-50%" width="200%" height="200%">
                  <feGaussianBlur in="SourceAlpha" stdDeviation="3"/>
                  <feOffset dx="0" dy="2" result="offsetblur"/>
                  <feComponentTransfer>
                    <feFuncA type="linear" slope="0.5"/>
                  </feComponentTransfer>
                  <feMerge>
                    <feMergeNode/>
                    <feMergeNode in="SourceGraphic"/>
                  </feMerge>
                </filter>
                <filter id="shadow" x="-50%" y="-50%" width="200%" height="200%">
                  <feGaussianBlur in="SourceAlpha" stdDeviation="5"/>
                  <feOffset dx="0" dy="4" result="offsetblur"/>
                  <feComponentTransfer>
                    <feFuncA type="linear" slope="0.6"/>
                  </feComponentTransfer>
                  <feMerge>
                    <feMergeNode/>
                    <feMergeNode in="SourceGraphic"/>
                  </feMerge>
                </filter>
                <linearGradient id="dotGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#f472b6" />
                  <stop offset="100%" stopColor="#a855f7" />
                </linearGradient>
                <linearGradient id="fullGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#ff6b6b" stopOpacity="0.7" />
                  <stop offset="10%" stopColor="#ff8c6b" stopOpacity="0.7" />
                  <stop offset="20%" stopColor="#ffad6b" stopOpacity="0.7" />
                  <stop offset="30%" stopColor="#ffc96b" stopOpacity="0.7" />
                  <stop offset="40%" stopColor="#ffe66b" stopOpacity="0.7" />
                  <stop offset="50%" stopColor="#ffff6b" stopOpacity="0.7" />
                  <stop offset="60%" stopColor="#d4ff6b" stopOpacity="0.7" />
                  <stop offset="70%" stopColor="#a8ff6b" stopOpacity="0.7" />
                  <stop offset="80%" stopColor="#7dff6b" stopOpacity="0.7" />
                  <stop offset="90%" stopColor="#6bff8c" stopOpacity="0.7" />
                  <stop offset="100%" stopColor="#6bffad" stopOpacity="0.7" />
                </linearGradient>
              </defs>
              
              <rect x="100" y="0" width="800" height="600" fill="url(#fullGradient)" />
              
              <line x1="100" y1="200" x2="900" y2="200" stroke="#cbd5e1" strokeWidth="1" strokeDasharray="5,5" opacity="0.4" />
              <line x1="100" y1="400" x2="900" y2="400" stroke="#cbd5e1" strokeWidth="1" strokeDasharray="5,5" opacity="0.4" />
              <line x1="300" y1="0" x2="300" y2="600" stroke="#cbd5e1" strokeWidth="2" strokeDasharray="5,5" opacity="0.4" />
              <line x1="500" y1="0" x2="500" y2="600" stroke="#cbd5e1" strokeWidth="2" strokeDasharray="5,5" opacity="0.4" />
              <line x1="700" y1="0" x2="700" y2="600" stroke="#cbd5e1" strokeWidth="2" strokeDasharray="5,5" opacity="0.4" />
              
              <rect x="100" y="0" width="800" height="600" fill="none" stroke="url(#borderGradient)" strokeWidth="3" rx="10" />
              
              <text x="80" y="100" fontSize="24" textAnchor="end" dominantBaseline="middle" fill="#e2e8f0" fontWeight="700" letterSpacing="-0.5">Pro</text>
              <text x="80" y="300" fontSize="24" textAnchor="end" dominantBaseline="middle" fill="#e2e8f0" fontWeight="700" letterSpacing="-0.5">Dev</text>
              <text x="80" y="500" fontSize="24" textAnchor="end" dominantBaseline="middle" fill="#e2e8f0" fontWeight="700" letterSpacing="-0.5">Flow</text>
              
              <text x="200" y="635" fontSize="20" textAnchor="middle" fill="#cbd5e1" fontWeight="600">Ideation</text>
              <text x="400" y="635" fontSize="20" textAnchor="middle" fill="#cbd5e1" fontWeight="600">Direction</text>
              <text x="600" y="635" fontSize="20" textAnchor="middle" fill="#cbd5e1" fontWeight="600">World Build</text>
              <text x="800" y="635" fontSize="20" textAnchor="middle" fill="#cbd5e1" fontWeight="600">Rollout</text>
              
              <text x="233.33" y="680" fontSize="32" textAnchor="middle" fill="#ff6b6b" fontWeight="800" letterSpacing="-1">Curation</text>
              <text x="500" y="680" fontSize="32" textAnchor="middle" fill="#ffff6b" fontWeight="800" letterSpacing="-1">Creative</text>
              <text x="766.67" y="680" fontSize="32" textAnchor="middle" fill="#6bffad" fontWeight="800" letterSpacing="-1">Marketing</text>
              
              {artists.map((artist, index) => {
                const isDragging = draggingIndex === index;
                return (
                  <g 
                    key={artist.id}
                    onMouseDown={() => handleMouseDown(index, artist.id)}
                    style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
                  >
                    <circle 
                      cx={artist.x} 
                      cy={artist.y} 
                      r="12" 
                      fill="url(#dotGradient)" 
                      stroke="#fff" 
                      strokeWidth="3"
                      opacity={isDragging ? 0.9 : 1}
                      filter={isDragging ? 'url(#shadow)' : 'url(#dropShadow)'}
                    />
                    <text 
                      x={artist.x} 
                      y={artist.y - 20} 
                      fontSize="16" 
                      textAnchor="middle" 
                      fill="#f1f5f9"
                      fontWeight="800"
                      letterSpacing="-0.5"
                      style={{ pointerEvents: 'none', userSelect: 'none' }}
                      opacity={isDragging ? 0.9 : 1}
                    >
                      {artist.name}
                    </text>
                  </g>
                );
              })}
            </svg>
          </div>
        </div>

        <div className="bg-slate-900/50 backdrop-blur-xl rounded-3xl shadow-2xl border border-slate-800 p-8 w-full">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-3xl font-black text-slate-100">Add Artist</h2>
            {saveStatus && (
              <div className="bg-slate-700 text-slate-100 font-bold py-2 px-6 rounded-lg transition duration-200 flex items-center gap-2">
                {saveStatus === 'saved' && <span>✓ Saved</span>}
                {saveStatus === 'error' && <span>✗ Error</span>}
              </div>
            )}
          </div>
          
          <div className="flex gap-4 mb-6">
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && addArtist()}
              placeholder="Enter artist name"
              className="flex-1 px-5 py-4 bg-slate-800/50 border-2 border-slate-700 text-slate-100 placeholder-slate-500 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition text-lg"
            />
            <button
              onClick={addArtist}
              className="bg-slate-700 hover:bg-slate-600 text-slate-100 font-black py-4 px-8 rounded-xl transition duration-200 shadow-lg hover:shadow-xl transform hover:scale-[1.02] uppercase tracking-wide"
            >
              Add Artist
            </button>
          </div>
          
          <p className="text-slate-400 text-sm">New artists are added to Flow → Ideation. Drag them to reposition.</p>
          
          {artists.length > 0 && (
            <div className="mt-8">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-black text-slate-100">Artists on Matrix</h3>
                <span className="bg-slate-700 text-slate-100 text-sm font-black px-4 py-1.5 rounded-full">
                  {artists.length}
                </span>
              </div>
              <div className="space-y-3 max-h-64 overflow-y-auto pr-2">
                {artists.map((artist, index) => {
                  const { gridX, gridY } = getGridPosition(artist);
                  return (
                    <div key={artist.id} className="flex items-center justify-between bg-slate-800/50 backdrop-blur p-4 rounded-xl border border-slate-700 hover:border-purple-500/50 transition group">
                      <span className="font-bold text-slate-100">
                        {artist.name} <span className="text-slate-500 font-normal text-sm">· {xPositions[gridX]}, {yPositions[gridY]}</span>
                      </span>
                      <button
                        onClick={() => removeArtist(artist.id)}
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10 p-2 rounded-lg transition opacity-0 group-hover:opacity-100"
                      >
                        <X size={20} />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default App;